import React, { useRef } from 'react';
import { Box, Typography, Button, IconButton, Select, MenuItem, TextField, RadioGroup, FormControlLabel, Radio, InputAdornment } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormContext } from '../context/FormContext';
import { fileToBase64 } from '../utils/fileUtils';
import {
    DEVELOPMENT_STATUS_OPTIONS, DEVELOPMENT_STATUS_OPTIONS_MANDIBLE, ERUPTION_STATUS_OPTIONS, IMPACTION_TYPE_OPTIONS, IMPACTION_TYPE_OPTIONS_MANDIBLE,
    EXTENSION_SUPERIOR_OPTIONS, EXTENSION_INFERIOR_OPTIONS, EXTENSION_MESIAL_OPTIONS, EXTENSION_MESIAL_OPTIONS_MANDIBLE,
    EXTENSION_DISTAL_OPTIONS, EXTENSION_BUCCOLINGUAL_OPTIONS, BUCCOLINGUAL_CROWN_OPTIONS, BUCCOLINGUAL_ROOT_OPTIONS,
    CROWN_SHAPE_OPTIONS, CROWN_MARGIN_OPTIONS, CROWN_DEFECT_OPTIONS,
    CROWN_ALIGNMENT_OPTIONS, FOLLICULAR_CHANGE_OPTIONS, CRESTAL_BONE_OPTIONS,
    PREVIOUS_INTERVENTION_OPTIONS,
    ECTOPIC_LOCATION_OPTIONS
} from './ImpactionConstants';
import ImpactionTextPreview from './ImpactionTextPreview';
import {
    generatePositionSectionText, generateImpactionDetailsText,
    generateExtensionText, generateCrownSectionText, generateCrestalBoneText
} from '../utils/impactionTextGenerator';

import MmInput from './MmInput';

// Helper for consistent label styling
const FieldLabel = ({ children }) => (
    <Typography sx={{ fontSize: '13px', mb: '5px' }}>
        {children}
    </Typography>
);

const ImpactionTab_Position = ({ selectedTooth, setSelectedTooth }) => {
    const { state, actions } = useFormContext();
    const toothData = state.impactionData?.[selectedTooth] || {};
    const positionData = toothData.position || {};

    const availableTeeth = ['38', '48'];

    const updateData = (field, value) => {
        actions.setImpactionData({
            ...state.impactionData,
            [selectedTooth]: {
                ...toothData,
                position: {
                    ...positionData,
                    [field]: value
                }
            }
        });
    };

    const handleToothSelection = (event) => {
        const newTooth = event.target.value;
        if (newTooth && setSelectedTooth) {
            setSelectedTooth(newTooth);
        }
    };

    const handleImageUpload = async (key) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            try {
                const base64 = await fileToBase64(file);
                // Store object to match structure: { base64, name, type }
                const fileData = { base64, name: file.name, type: file.type };
                updateData(key, fileData);
            } catch (error) {
                console.error("Error converting file to Base64:", error);
            }
        };
        input.click();
    };

    const handleRemoveImage = (key) => {
        updateData(key, null);
    };

    const renderImageSection = (label, key, fileData, height = '250px') => {
        const preview = fileData ? fileData.base64 : null;

        return (
            <Box sx={{ width: '350px', flexShrink: 0 }}>
                {/* Image Container */}
                <Box sx={{ position: 'relative', mb: '15px' }}>
                    <Box
                        sx={{
                            width: '350px',
                            height: height,
                            border: '2px solid black',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.200',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        {/* Remove button */}
                        {preview && (
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveImage(key)}
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    bgcolor: 'background.paper',
                                    '&:hover': { bgcolor: 'grey.100' },
                                    zIndex: 1
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}

                        {preview ? (
                            <img
                                src={preview}
                                alt={label}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {label}
                            </Typography>
                        )}
                    </Box>

                    {fileData && (
                        <Typography variant="caption" sx={{ color: 'success.main', fontSize: 10, display: 'block', mt: 0.5 }}>
                            {fileData.name}
                        </Typography>
                    )}
                </Box>

                <Button
                    variant={preview ? "outlined" : "contained"}
                    fullWidth
                    color={preview ? "secondary" : "primary"}
                    onClick={() => handleImageUpload(key)}
                    sx={{ mb: '15px' }}
                >
                    {preview ? 'Replace Image' : 'Select Image'}
                </Button>
            </Box>
        );
    };

    const sectionBoxStyle = { border: '1px solid #e0e0e0', borderRadius: '4px', mb: '20px', p: '20px' };
    const sectionHeaderStyle = {
        fontSize: '18px',
        fontWeight: 'bold',
        mb: '15px',
        pb: '10px'
    };
    const inputStyle = {
        // Style to match the look in RegionMeasurements roughly, but using MUI components
        '& .MuiOutlinedInput-root': {
            borderRadius: 0, // angular look
        },
        '& .MuiOutlinedInput-input': {
            padding: '8px 14px',
            fontSize: '14px',
        }
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
                Tooth Position and Development ({selectedTooth})
            </Typography>

            {/* SECTION 1: POSITION & DEVELOPMENT */}
            <Box sx={sectionBoxStyle}>
                <Typography sx={sectionHeaderStyle}>
                    POSITION & DEVELOPMENT
                </Typography>

                <Box sx={{ display: 'flex', gap: '25px', flexDirection: { xs: 'column', lg: 'row' } }}>
                    {/* Left: Panoramic Image */}
                    {renderImageSection('Panoramic Image', 'panImage', positionData.panImage)}

                    {/* Right: Inputs */}
                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', alignContent: 'start' }}>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Active Tooth</FieldLabel>
                            <Select
                                value={selectedTooth}
                                onChange={handleToothSelection}
                                size="small"
                                sx={inputStyle}
                            >
                                {availableTeeth.map((tooth) => (
                                    <MenuItem key={tooth} value={tooth}>Tooth {tooth}</MenuItem>
                                ))}
                            </Select>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Development Status</FieldLabel>
                            <Select
                                value={positionData.development || ''}
                                onChange={(e) => updateData('development', e.target.value)}
                                displayEmpty
                                size="small"
                                sx={inputStyle}
                            >
                                {DEVELOPMENT_STATUS_OPTIONS_MANDIBLE.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Eruption Status</FieldLabel>
                            <Select
                                value={positionData.eruption || ''}
                                onChange={(e) => updateData('eruption', e.target.value)}
                                displayEmpty
                                size="small"
                                sx={inputStyle}
                            >
                                {ERUPTION_STATUS_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>

                    </Box>
                </Box>
                <ImpactionTextPreview text={generatePositionSectionText(positionData)} />
            </Box>

            {/* SECTION 2: IMPACTION DETAILS (Split for images) */}
            <Box sx={sectionBoxStyle}>
                <Typography sx={sectionHeaderStyle}>
                    IMPACTION DETAILS
                </Typography>

                <Box sx={{ display: 'flex', gap: '25px', flexDirection: { xs: 'column', lg: 'row' } }}>
                    {/* Left: Images Column - We stack Sagittal and Angulation here or put them side by side? 
                        The region tab implies one main image column. 
                        Let's put Sagittal as the main one, and maybe Angulation below it or next to it.
                        Given 350px width, stacking is safer. */}
                    <Box>
                        {renderImageSection('Sagittal Image', 'sagittalImage', positionData.sagittalImage)}
                        <Typography variant="body2" sx={{ my: 1, fontWeight: 'bold' }}>Angulation View:</Typography>
                        {renderImageSection('Angulation Image', 'angulationImage', positionData.angulationImage, '200px')}
                    </Box>

                    {/* Right: Inputs */}
                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', alignContent: 'start' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gridColumn: '1 / -1' }}>
                            <FieldLabel>Impaction Type</FieldLabel>
                            <Select
                                value={positionData.type || ''}
                                onChange={(e) => updateData('type', e.target.value)}
                                displayEmpty
                                size="small"
                                sx={inputStyle}
                            >
                                {IMPACTION_TYPE_OPTIONS_MANDIBLE.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>

                        {/* CONDITIONAL FIELDS BASED ON TYPE */}
                        {positionData.type === 'Vertical' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gridColumn: '1 / -1', p: 1, bgcolor: 'grey.50', border: '1px dashed #ccc' }}>
                                <FieldLabel>Aligned with Second Molar</FieldLabel>
                                <RadioGroup
                                    row
                                    value={positionData.verticalAlignTooth || ''}
                                    onChange={(e) => updateData('verticalAlignTooth', e.target.value)}
                                >
                                    <FormControlLabel value="37" control={<Radio size="small" />} label="37" />
                                    <FormControlLabel value="47" control={<Radio size="small" />} label="47" />
                                </RadioGroup>
                            </Box>
                        )}

                        {positionData.type === 'Mesioangular' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', p: 1, bgcolor: 'grey.50', border: '1px dashed #ccc' }}>
                                <FieldLabel>Tilt Angle (approx.)</FieldLabel>
                                <MmInput
                                    value={positionData.mesioAngle}
                                    onChange={(e) => updateData('mesioAngle', e.target.value)}
                                    placeholder="deg"
                                    adornment="°"
                                    width="110px"
                                />
                            </Box>
                        )}

                        {positionData.type === 'Distoangular' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', p: 1, bgcolor: 'grey.50', border: '1px dashed #ccc' }}>
                                <FieldLabel>Tilt Angle (approx.)</FieldLabel>
                                <MmInput
                                    value={positionData.distoAngle}
                                    onChange={(e) => updateData('distoAngle', e.target.value)}
                                    placeholder="deg"
                                    adornment="°"
                                    width="110px"
                                />
                            </Box>
                        )}

                        {positionData.type === 'Ectopic' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gridColumn: '1 / -1', p: 1, bgcolor: 'grey.50', border: '1px dashed #ccc' }}>
                                <FieldLabel>Location</FieldLabel>
                                <Select
                                    value={positionData.ectopicLocation || ''}
                                    onChange={(e) => updateData('ectopicLocation', e.target.value)}
                                    displayEmpty
                                    size="small"
                                    sx={inputStyle}
                                >
                                    {ECTOPIC_LOCATION_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                            </Box>
                        )}

                    </Box>
                </Box>
                <ImpactionTextPreview text={generateImpactionDetailsText(positionData)} />
            </Box>

            {/* SECTION 3: EXTENSION OF TOOTH */}
            <Box sx={sectionBoxStyle}>
                <Typography sx={sectionHeaderStyle}>
                    EXTENSION OF TOOTH
                </Typography>

                <Box sx={{ display: 'flex', gap: '25px', flexDirection: { xs: 'column', lg: 'row' } }}>
                    {/* Left: Extension Image */}
                    {renderImageSection('Extension Image', 'extensionsImage', positionData.extensionsImage)}

                    {/* Right: Inputs */}
                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', alignContent: 'start' }}>

                        {/* Superiorly Row */}
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Superiorly</FieldLabel>
                            <Box display="flex" gap={1}>
                                <Select
                                    value={positionData.extSuperior || ''}
                                    onChange={(e) => updateData('extSuperior', e.target.value)}
                                    displayEmpty
                                    size="small"
                                    sx={{ ...inputStyle, flexGrow: 1 }}
                                >
                                    {EXTENSION_SUPERIOR_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                                <MmInput
                                    value={positionData.extSuperiorMm}
                                    onChange={(e) => updateData('extSuperiorMm', e.target.value)}
                                    width="110px"
                                />
                            </Box>
                        </Box>

                        {/* Inferiorly Row */}
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Inferiorly</FieldLabel>
                            <Box display="flex" gap={1}>
                                <Select
                                    value={positionData.extInferior || ''}
                                    onChange={(e) => updateData('extInferior', e.target.value)}
                                    displayEmpty
                                    size="small"
                                    sx={{ ...inputStyle, flexGrow: 1 }}
                                >
                                    {EXTENSION_INFERIOR_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                                <MmInput
                                    value={positionData.extInferiorMm}
                                    onChange={(e) => updateData('extInferiorMm', e.target.value)}
                                    width="110px"
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Mesially</FieldLabel>
                            <Box display="flex" gap={1}>
                                <Select
                                    value={positionData.extMesial || ''}
                                    onChange={(e) => updateData('extMesial', e.target.value)}
                                    displayEmpty
                                    size="small"
                                    sx={{ ...inputStyle, flexGrow: 1 }}
                                >
                                    {EXTENSION_MESIAL_OPTIONS_MANDIBLE.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                                <MmInput
                                    value={positionData.extMesialMm}
                                    onChange={(e) => updateData('extMesialMm', e.target.value)}
                                    width="110px"
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Distally</FieldLabel>
                            <Box display="flex" gap={1}>
                                <Select
                                    value={positionData.extDistal || ''}
                                    onChange={(e) => updateData('extDistal', e.target.value)}
                                    displayEmpty
                                    size="small"
                                    sx={{ ...inputStyle, flexGrow: 1 }}
                                >
                                    {EXTENSION_DISTAL_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                                <MmInput
                                    value={positionData.extDistalMm}
                                    onChange={(e) => updateData('extDistalMm', e.target.value)}
                                    width="110px"
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gridColumn: '1 / -1' }}>
                            <FieldLabel>Buccolingual - Crown</FieldLabel>
                            <Select
                                value={positionData.extBuccolingualCrown || ''}
                                onChange={(e) => updateData('extBuccolingualCrown', e.target.value)}
                                displayEmpty
                                size="small"
                                sx={inputStyle}
                            >
                                {BUCCOLINGUAL_CROWN_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gridColumn: '1 / -1' }}>
                            <FieldLabel>Buccolingual - Root</FieldLabel>
                            <Select
                                value={positionData.extBuccolingualRoot || ''}
                                onChange={(e) => updateData('extBuccolingualRoot', e.target.value)}
                                displayEmpty
                                size="small"
                                sx={inputStyle}
                            >
                                {BUCCOLINGUAL_ROOT_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>

                    </Box>
                </Box>
                <ImpactionTextPreview text={generateExtensionText(positionData)} />
            </Box>

            {/* SECTION 4: CROWN FEATURES */}
            <Box sx={sectionBoxStyle}>
                <Typography sx={sectionHeaderStyle}>
                    CROWN FEATURES
                </Typography>

                <Box sx={{ display: 'flex', gap: '25px', flexDirection: { xs: 'column', lg: 'row' } }}>
                    {/* Left: Image */}
                    {renderImageSection('Crown / Follicular Image', 'crownImage', positionData.crownImage)}

                    {/* Right: Inputs */}
                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', alignContent: 'start' }}>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Shape</FieldLabel>
                            <Select
                                value={positionData.crownShape || ''}
                                onChange={(e) => updateData('crownShape', e.target.value)}
                                displayEmpty
                                size="small"
                                sx={inputStyle}
                            >
                                {CROWN_SHAPE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Margins</FieldLabel>
                            <Select
                                value={positionData.crownMargins || ''}
                                onChange={(e) => updateData('crownMargins', e.target.value)}
                                displayEmpty
                                size="small"
                                sx={inputStyle}
                            >
                                {CROWN_MARGIN_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Defects</FieldLabel>
                            <Select
                                value={positionData.crownDefects || ''}
                                onChange={(e) => updateData('crownDefects', e.target.value)}
                                displayEmpty
                                size="small"
                                sx={inputStyle}
                            >
                                {CROWN_DEFECT_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Alignment/Orientation</FieldLabel>
                            <Select
                                value={positionData.crownAlignment || ''}
                                onChange={(e) => updateData('crownAlignment', e.target.value)}
                                displayEmpty
                                size="small"
                                sx={inputStyle}
                            >
                                {CROWN_ALIGNMENT_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Follicular Space</FieldLabel>
                            <Select
                                value={positionData.follicularChanges || ''}
                                onChange={(e) => updateData('follicularChanges', e.target.value)}
                                displayEmpty
                                size="small"
                                sx={inputStyle}
                            >
                                {FOLLICULAR_CHANGE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>

                            {positionData.follicularChanges === 'Enlarged' && (
                                <Box sx={{ mt: 1, p: 1, border: '1px dashed #ccc', bgcolor: 'grey.50', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="caption" color="textSecondary">Follicular Dimensions:</Typography>
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Typography variant="caption">Superoinferiorly</Typography>
                                        <MmInput value={positionData.follicularMmSi} onChange={(e) => updateData('follicularMmSi', e.target.value)} width="110px" />
                                    </Box>
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Typography variant="caption">Mesiodistally</Typography>
                                        <MmInput value={positionData.follicularMmMd} onChange={(e) => updateData('follicularMmMd', e.target.value)} width="110px" />
                                    </Box>
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Typography variant="caption">Buccolingually</Typography>
                                        <MmInput value={positionData.follicularMmBl} onChange={(e) => updateData('follicularMmBl', e.target.value)} width="110px" />
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Previous Interventions</FieldLabel>
                            <Select
                                value={positionData.previousInterventions || ''}
                                onChange={(e) => updateData('previousInterventions', e.target.value)}
                                displayEmpty
                                size="small"
                                sx={inputStyle}
                            >
                                {PREVIOUS_INTERVENTION_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>



                    </Box>
                </Box>
                <ImpactionTextPreview text={generateCrownSectionText(positionData)} />
            </Box>



        </Box>
    );
};

export default ImpactionTab_Position;
